import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	ReplyToAnnotationEdit,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxAnnotationAnnotation
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import AnnotationReplyDialogFields from "../../field/annotation/AnnotationReplyDialogFields";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class AnnotationReplyEditDialogDefinition extends PortalDialog<ToolboxAnnotationAnnotation,
	PortalDialogProperties<ToolboxAnnotationAnnotation>,
	PortalDialogState<ToolboxAnnotationAnnotation>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<AnnotationReplyDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editAnnotation, Functions.getEditAnnotationDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ANNOTATION_UNDERLINE_EDIT, signatureEditProfile, Intention.editingAnnotations,
				selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["annotation-reply-edit-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.annotation_reply.edit"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let errors: Map<AnnotationReplyDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.comment?.edit?.replyTo)) {
			return null;
		}

		let annotationParameter: ReplyToAnnotationEdit = parameter!.comment!.edit!.replyTo![0];

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationReplyDialogFields.CREATOR)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-creator">
							<Translate {...this.props} data="annotation.properties.creator.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-creator"
									   className={errors.has(AnnotationReplyDialogFields.CREATOR) ? "p-invalid" : ""}
									   tooltip={errors.has(AnnotationReplyDialogFields.CREATOR) ? Translate.value(
										   errors.get(AnnotationReplyDialogFields.CREATOR)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   annotationParameter.creator, ReplyToAnnotationEdit.getCreatorDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationReplyDialogFields.CREATOR, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(AnnotationReplyDialogFields.CREATOR) ?
								Translate.value(
									errors.get(AnnotationReplyDialogFields.CREATOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationReplyDialogFields.SUBJECT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-subject">
							<Translate {...this.props} data="annotation.properties.subject.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputText id="control-subject"
									   value={FunctionHelper.applyValue(
										   annotationParameter.subject, ReplyToAnnotationEdit.getSubjectDefault()
									   )}
									   onInput={(e) => this.onChange(
										   AnnotationReplyDialogFields.SUBJECT, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(AnnotationReplyDialogFields.CONTENTS)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-contents">
							<Translate {...this.props} data="annotation.properties.comment" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<InputTextarea rows={3} id="control-contents"
										   value={FunctionHelper.applyValue(
											   annotationParameter.comment, ReplyToAnnotationEdit.getCommentDefault()
										   )}
										   onInput={(e) => this.onChange(
											   AnnotationReplyDialogFields.CONTENTS, e.currentTarget.value
										   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: AnnotationReplyDialogFields, value: any): void {
		let parameter: ToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: ReplyToAnnotationEdit = parameter.comment!.edit!.replyTo![0];
		let errors: Map<AnnotationReplyDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case AnnotationReplyDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case AnnotationReplyDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case AnnotationReplyDialogFields.CONTENTS:
				annotationParameter.comment = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-edit"}/>
					<Translate {...this.props} data="annotation.edit" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			annotation: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.annotation.edit.reply", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ANNOTATION_REPLY_EDIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const AnnotationReplyEditDialog = React.memo(AnnotationReplyEditDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default AnnotationReplyEditDialog;